import { Map } from 'maplibre-gl';
import { usePathname } from 'next/navigation';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMap } from 'react-map-gl/maplibre';
import { useDispatch } from 'react-redux';

import { PLAT_MAP_URL } from '@/constants/urls';
import {
  type CoreVectorSourceState,
  emptyCoreVectorSourceState,
  useCoreVectorSourceState,
} from '@/features/mapbox/provider/useCoreVectorSourceState';
import {
  emptyGovernmentDistrictLayerState,
  type GovernmentDistrictLayerState,
  useGovernmentDistrictLayerState,
} from '@/features/mapbox/provider/useGovernmentDistrictLayerState';
import {
  emptyMapboxBaseMapState,
  type MapboxBaseMapState,
  useMapboxBaseMapState,
} from '@/features/mapbox/provider/useMapboxBaseMapState';
import {
  emptyMapboxHelperUtils,
  type MapboxHelperUtils,
  useMapboxHelperUtils,
} from '@/features/mapbox/useMapboxHelperUtils';
import {
  emptyParcelFsaBaseLayersState,
  type ParcelFsaBaseLayersState,
  useParcelFsaBaseLayers,
} from '@/features/navigation/hooks/useParcelFsaBaseLayers';
import { ReactSetState } from '@/models/reactTypes';
import { setDrawTool } from '@/redux/slices/drawV2Slice';
import { unimplementedFunction } from '@/utils/unimplementedFunction';

export const MAX_2D_MODE_ZOOM = 18;
export const MIN_2D_MODE_ZOOM = 3.5;
export const MAX_3D_MODE_ZOOM = 22;
export const MIN_3D_MODE_ZOOM = 3.5;

type MapBoxContextType = {
  isCursorOverMap: boolean;
  setIsCursorOverMap: ReactSetState<boolean>;
  baseMapLayersLoaded: boolean;
  setBaseMapLayersLoaded: ReactSetState<boolean>;
  setIsUsingQuickMeasure: ReactSetState<boolean>;
  isUsingQuickMeasure: boolean;
  mapInstance: Map | undefined;
  areBaseSourcesLoaded: boolean;
  setAreBaseSourcesLoaded: ReactSetState<boolean>;
  utils: MapboxHelperUtils;
  isMapInstanceLoaded: boolean;
  setIsMapInstanceLoaded: ReactSetState<boolean>;
  layers: ParcelFsaBaseLayersState &
    GovernmentDistrictLayerState &
    MapboxBaseMapState & {};
  sources: CoreVectorSourceState & {};
};

const defaultMapboxContext: MapBoxContextType = {
  areBaseSourcesLoaded: false,
  baseMapLayersLoaded: false,
  isCursorOverMap: false,
  isMapInstanceLoaded: false,
  isUsingQuickMeasure: false,
  layers: {
    ...emptyParcelFsaBaseLayersState,
    ...emptyGovernmentDistrictLayerState,
    ...emptyMapboxBaseMapState,
  },
  mapInstance: undefined,
  setAreBaseSourcesLoaded: unimplementedFunction,
  setBaseMapLayersLoaded: unimplementedFunction,
  setIsCursorOverMap: unimplementedFunction,
  setIsMapInstanceLoaded: unimplementedFunction,
  setIsUsingQuickMeasure: unimplementedFunction,
  sources: {
    ...emptyCoreVectorSourceState,
  },
  utils: emptyMapboxHelperUtils,
};

export const MapboxContext =
  createContext<MapBoxContextType>(defaultMapboxContext);

export const STANDARD_ANIMATION_DURATION = 1000;

type Props = {
  children: ReactNode;
};

export const MapboxProvider: FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const { map } = useMap();

  const [isCursorOverMap, setIsCursorOverMap] = useState<boolean>(false);
  const [areBaseSourcesLoaded, setAreBaseSourcesLoaded] =
    useState<boolean>(false);
  const [baseMapLayersLoaded, setBaseMapLayersLoaded] =
    useState<boolean>(false);
  const [isUsingQuickMeasure, setIsUsingQuickMeasure] =
    useState<boolean>(false);

  const pathname = usePathname();

  const [isMapInstanceLoaded, setIsMapInstanceLoaded] =
    useState<boolean>(false);

  const mapInstance: Map | undefined = useMemo(() => {
    if (isMapInstanceLoaded && map) {
      return map?.getMap() as any;
    }
    return undefined;
  }, [isMapInstanceLoaded, map]);

  useEffect(() => {
    if (!pathname?.includes(PLAT_MAP_URL)) {
      setIsMapInstanceLoaded(false);
      dispatch(setDrawTool(undefined));
    }
  }, [mapInstance, pathname]);

  const mapboxHelperFunctions = useMapboxHelperUtils(
    mapInstance,
    isMapInstanceLoaded
  );

  const parcelFsaLayerState = useParcelFsaBaseLayers();
  const governmentDistrictLayerState = useGovernmentDistrictLayerState();

  const coreVectorSourceState = useCoreVectorSourceState();

  const mapboxBaseMapState = useMapboxBaseMapState(
    mapInstance,
    isMapInstanceLoaded,
    setBaseMapLayersLoaded
  );

  const value: MapBoxContextType = useMemo(
    () => ({
      areBaseSourcesLoaded,
      baseMapLayersLoaded,
      isCursorOverMap,
      isMapInstanceLoaded,
      isUsingQuickMeasure,
      layers: {
        ...parcelFsaLayerState,
        ...governmentDistrictLayerState,
        ...mapboxBaseMapState,
      },
      mapInstance,
      setAreBaseSourcesLoaded,
      setBaseMapLayersLoaded,
      setIsCursorOverMap,
      setIsMapInstanceLoaded,
      setIsUsingQuickMeasure,
      sources: {
        ...coreVectorSourceState,
      },
      utils: mapboxHelperFunctions,
    }),
    [
      areBaseSourcesLoaded,
      baseMapLayersLoaded,
      isCursorOverMap,
      isMapInstanceLoaded,
      isUsingQuickMeasure,
      parcelFsaLayerState,
      governmentDistrictLayerState,
      mapboxBaseMapState,
      mapInstance,
      coreVectorSourceState,
      mapboxHelperFunctions,
    ]
  );

  return (
    <MapboxContext.Provider value={value}>{children}</MapboxContext.Provider>
  );
};

export const useMapboxContext = () => useContext(MapboxContext);
